import { apiClient, type ApiClientError } from '@/lib/api';
import { createIdempotentMutationHook } from '@/src/hooks/useApiMutation';

export type OAuthProvider = 'github' | 'microsoft' | 'linear' | 'notion';

export interface LinkProviderAccountVariables {
  provider: OAuthProvider;
  ticketId: string;
}

export interface UseLinkProviderAccountMutationOptions {
  onSuccess?: () => void;
  onRequestAccessRequired?: (login: string, error: ApiClientError) => void;
}

/**
 * Mutation function for linking provider account.
 */
async function linkProviderAccountMutation(
  variables: LinkProviderAccountVariables,
  { idempotencyKey }: { idempotencyKey: string }
): Promise<unknown> {
  const { provider, ticketId } = variables;

  return apiClient.post(
    `/api/integrations/${provider}/link`,
    {
      ticketId,
    },
    undefined,
    undefined,
    { idempotencyKey }
  );
}

/**
 * React Query hook for linking provider accounts with automatic idempotency.
 *
 * Uses factory pattern with automatic error handling (toast + Sentry).
 * Handles special case for REQUEST_ACCESS_REQUIRED errors.
 *
 * @example
 * const linkAccount = useLinkProviderAccountMutation({
 *   onSuccess: () => console.log('Linked'),
 *   onRequestAccessRequired: (login) => openRequestDialog(login)
 * });
 * linkAccount.mutate({
 *   provider: 'github',
 *   ticketId: 'ticket_123'
 * });
 */
export const useLinkProviderAccountMutation = createIdempotentMutationHook<
  unknown,
  LinkProviderAccountVariables
>(linkProviderAccountMutation);
